import { isEmpty } from 'lodash';
import { useIntl } from 'react-intl';
import { targetingConstants } from 'sonata-platform-components';
import { Checkbox, MultipleTagSelector, RadioButtonsGroup } from 'sonata-react-components';
import { getDevicePlatformsByUser } from 'sonata-static-storage/lib/helpers/targeting.helpers';
import { checkUserResourcePermissions } from 'sonata-static-storage/lib/helpers/user.helpers';
import { connectionsDictionary } from 'sonata-static-storage/lib/samples/connectionsDictionary.samples';
import { CampaignFamilyEnum } from 'sonata-static-storage/lib/schemas/campaign/campaign.schema';
import { DevicePlatformEnum } from 'sonata-static-storage/lib/schemas/campaign/targeting/devicePlatform.schema';

import { useTargetingContext } from '@/contexts/Targeting/TargetingContext';

const { DEFAULT_MOBILE_DEVICE_AND_CONNECTIVITY } = targetingConstants;

import { FormExpandableSection } from '@/components/common/FormExpandableSection/FormExpandableSection';
import { fetchDeviceCarriers } from '@/resources/devices.resources';
import { DO_NOT_DISTURB_TARGETING_SECTION_KEY } from '../common/common';
import { SelectionableSectionWithConfirmation } from '../common/SelectionableSectionWithConfirmation';
import { Carriers } from '../Device/Carriers/Carriers';
import { getMobileDeviceTags } from './helpers/getSectionHeaderTags.helper';

import './mobileDeviceAndConnectivity.styles.scss';

import MakeModelsMultiflightWrapper from '../Device/MakeModelsWrapper/MakeModelsMultiflightWrapper';
import { OsMultiflightWrapper } from '../Device/Os';

function MobileDeviceAndConnectivity() {
  const { formatMessage } = useIntl();
  const { countries, familyType, setProp, setUnknownIncluded, targeting, user, userRoles, resetSection } =
    useTargetingContext();

  const { carriers, channel, connectionTypes, deviceMakeModels, deviceOsV2, deviceTypes, unknownIncluded } = targeting;

  const enabledSections = {
    carriers: Boolean(carriers?.length),
    channel: Boolean(channel?.filter(({ enable }) => enable).length),
    connectionTypes: Boolean(connectionTypes?.length),
    deviceMakeModels: Boolean(deviceMakeModels?.length),
    deviceOsV2: Boolean(deviceOsV2?.length),
    deviceTypes: Boolean(deviceTypes?.length)
  };

  const deviceTypesOptions =
    familyType === CampaignFamilyEnum.ctv
      ? ['any', 'connected_tv', 'connected_device', 'stb']
      : ['any', 'mobile', 'tablet'];

  const radioButtonGroupItems = deviceTypesOptions.map(opt => ({
    icons:
      opt !== 'any' && familyType === CampaignFamilyEnum.standard
        ? [
            {
              icon: opt === 'tablet' ? 'fa-regular fa-tablet' : 'fa-regular fa-mobile',
              position: 'left' as any
            }
          ]
        : undefined,
    id: opt,
    text: formatMessage({
      defaultMessage: opt,
      id: `targeting.mobileDeviceAndConnectivity.deviceTypes.${opt}`
    })
  }));

  const deviceSelected = isEmpty(deviceTypes)
    ? radioButtonGroupItems.find(rb => rb.id === 'any')?.id
    : radioButtonGroupItems.find(rb => rb.id === deviceTypes[0])?.id;

  const unknownIncludedPermission = checkUserResourcePermissions('unknownIncluded', user, userRoles);

  const enabledDevicePlatforms =
    familyType === CampaignFamilyEnum.ctv
      ? [DevicePlatformEnum.ctv]
      : [DevicePlatformEnum.mobile, DevicePlatformEnum.desktop];
  const devicePlatforms = getDevicePlatformsByUser(user, userRoles, enabledDevicePlatforms);

  const connectionTypeItems = connectionsDictionary.map(connection => ({
    label: connection.name,
    value: connection.id
  }));

  const radioButtonListSourceTargetingConfig = ['source', 'site', 'app'].map(opt => ({
    id: opt,
    text: formatMessage({
      defaultMessage: opt,
      id: `targeting.mobileDeviceAndConnectivity.channel.${opt}`
    })
  }));

  const channelValues = channel.filter((item: Record<string, any>) => item.enable);

  const sourceTargetingSelected =
    channelValues.length === 2
      ? radioButtonListSourceTargetingConfig.find(rb => rb.id === 'source')?.id
      : radioButtonListSourceTargetingConfig.find(rb => rb.id === channelValues[0].name)?.id;

  const calcEntityValueFunctions = {
    channel: (selectedChannel: string) => {
      return channel.map(channelItem => ({
        ...channelItem,
        enable: selectedChannel === 'source' ? true : channelItem.name === selectedChannel
      }));
    },
    deviceTypes: (id: string) => {
      return id === 'any' ? [] : [id];
    }
  };

  const handleSectionValueChanges = (section: keyof typeof DEFAULT_MOBILE_DEVICE_AND_CONNECTIVITY, value: any) => {
    const newEntityValue =
      section in calcEntityValueFunctions
        ? calcEntityValueFunctions[section as keyof typeof calcEntityValueFunctions](value)
        : value;
    setProp(section, newEntityValue);
  };

  const handleSectionEnabledChange = (
    section: keyof typeof DEFAULT_MOBILE_DEVICE_AND_CONNECTIVITY,
    enabled: boolean
  ) => {
    if (!enabled) {
      const mapSection: Record<string, string> = {
        carriers: 'deviceCarrier',
        channel: 'channel',
        connectionTypes: 'deviceConnection',
        deviceMakeModels: 'makeModels',
        deviceOsV2: 'deviceOsName',
        deviceTypes: 'deviceType'
      };
      const newValue = structuredClone(DEFAULT_MOBILE_DEVICE_AND_CONNECTIVITY[section]);
      setProp(section, newValue);
      setUnknownIncluded(mapSection[section], false);
    }
  };

  const handleUnknownIncludeChange = (
    section: keyof (typeof DEFAULT_MOBILE_DEVICE_AND_CONNECTIVITY)['unknownIncluded'],
    value: boolean
  ) => {
    setUnknownIncluded(section, value);
  };

  const handleResetMobileDevice = () => {
    resetSection('mobileDeviceAndConnectivity');
  };

  const isSectionOpened =
    channel.filter((item: Record<string, any>) => item.enable).length === 1 ||
    !isEmpty(deviceTypes) ||
    deviceOsV2.length > 0 ||
    carriers.length > 0 ||
    connectionTypes.length > 0 ||
    deviceMakeModels.length > 0;
  const mobileDeviceTags = getMobileDeviceTags(deviceTypes, deviceOsV2, connectionTypes);

  return (
    <div id="mobileDeviceAndConnectivityBox">
      <FormExpandableSection
        defaultOpen={isSectionOpened}
        onReset={handleResetMobileDevice}
        preffixId="mobileDeviceAndConnectivity"
        tags={mobileDeviceTags}
        title={formatMessage({
          defaultMessage: 'Mobile Device and Connectivity',
          id: 'targeting.mobileDeviceAndConnectivity.title'
        })}
        tooltipText={formatMessage({
          defaultMessage:
            'Target specific operating systems, device types (e.g., smartphone, tablet), or internet connection methods.',
          id: 'targeting.mobileDeviceAndConnectivity.tooltip'
        })}
      >
        <div className="mobileDeviceAndConnectivity__deviceTypeContainer">
          <div className="mobileDeviceAndConnectivity__deviceTypeRadioButtonList">
            <RadioButtonsGroup
              id={'mobileDeviceAndConnectivityRadioButtonListDeviceType'}
              items={radioButtonGroupItems}
              onChange={(value: any) => handleSectionValueChanges('deviceTypes', value)}
              selectedItem={deviceSelected}
            />
          </div>
          {unknownIncludedPermission && deviceSelected !== 'any' && (
            <Checkbox
              active={unknownIncluded.deviceType}
              id={'mobileDeviceAndConnectivityCheckBoxInsideABoxDeviceType'}
              labelText={formatMessage({
                defaultMessage: 'Target unknown',
                id: 'multiflight.targetUnknown'
              })}
              onClickAction={(_id, checked) => handleUnknownIncludeChange('deviceType', checked)}
            />
          )}
        </div>
        <div className="mobileDeviceAndConnectivity__sourceContainer">
          <SelectionableSectionWithConfirmation
            id={'mobileDeviceAndConnectivity-sourceTargetinOptions'}
            isSectionEnabled={enabledSections.channel}
            label={formatMessage({
              defaultMessage: 'Source targeting options',
              id: 'targeting.mobileDeviceAndConnectivity.sections.targetingOptions'
            })}
            modalConfirm={{
              storageKey: DO_NOT_DISTURB_TARGETING_SECTION_KEY
            }}
            onSectionEnabledChange={(enabled: boolean) => handleSectionEnabledChange('channel', enabled)}
          >
            <div className="mobileDeviceAndConnectivity__radioButtonListSourceContainer">
              <RadioButtonsGroup
                className="mobileDeviceAndConnectivity__radioButtonListSource"
                id={'mobileDeviceAndConnectivityRadioButtonListChannel'}
                items={radioButtonListSourceTargetingConfig}
                onChange={(value: any) => handleSectionValueChanges('channel', value)}
                selectedItem={sourceTargetingSelected}
              />
              {unknownIncludedPermission && sourceTargetingSelected !== 'source' && (
                <Checkbox
                  active={unknownIncluded.channel}
                  id={'mobileDeviceAndConnectivityCheckBoxInsideABoxChannel'}
                  labelText={formatMessage({
                    defaultMessage: 'Target unknown',
                    id: 'multiflight.targetUnknown'
                  })}
                  onClickAction={(_id, checked) => handleUnknownIncludeChange('channel', checked)}
                />
              )}
            </div>
          </SelectionableSectionWithConfirmation>
        </div>
        <div className="mobileDeviceAndConnectivity__osContainer">
          <SelectionableSectionWithConfirmation
            id={'mobileDeviceAndConnectivity-OsTargetinOptions'}
            isSectionEnabled={enabledSections.deviceOsV2}
            label={formatMessage({
              defaultMessage: 'Os targeting options',
              id: 'targeting.mobileDeviceAndConnectivity.sections.targetingOsOptions'
            })}
            modalConfirm={{
              storageKey: DO_NOT_DISTURB_TARGETING_SECTION_KEY
            }}
            onSectionEnabledChange={(enabled: boolean) => handleSectionEnabledChange('deviceOsV2', enabled)}
          >
            <div className="mobileDeviceAndConnectivity__os">
              <OsMultiflightWrapper
                deviceTypes={devicePlatforms}
                id={'mobileDeviceAndConnectivity-deviceOs'}
                onDeviceOsChanged={values => handleSectionValueChanges('deviceOsV2', values)}
                onUnknownIncludedToggled={(value: boolean) => handleUnknownIncludeChange('deviceOsName', value)}
                selectedDeviceOs={deviceOsV2}
                unknownIncluded={unknownIncluded.deviceOsName}
                unknownIncludedPermission={unknownIncludedPermission}
              />
            </div>
          </SelectionableSectionWithConfirmation>
        </div>
        <div className="mobileDeviceAndConnectivity__deviceMake">
          <SelectionableSectionWithConfirmation
            id={'mobileDeviceAndConnectivityDeviceMakeTargetingOptions'}
            isSectionEnabled={enabledSections.deviceMakeModels}
            label={formatMessage({
              defaultMessage: 'Device Make targeting options',
              id: 'targeting.mobileDeviceAndConnectivity.deviceMake.label'
            })}
            modalConfirm={{
              storageKey: DO_NOT_DISTURB_TARGETING_SECTION_KEY
            }}
            onSectionEnabledChange={(enabled: boolean) => handleSectionEnabledChange('deviceMakeModels', enabled)}
          >
            <MakeModelsMultiflightWrapper
              className="mobileDeviceAndConnectivity__deviceMakeWrapper"
              deviceTypes={devicePlatforms}
              id={'mobileDeviceAndConnectivityDeviceMakeModelsSelectionTargetingOptions'}
              makeModels={deviceMakeModels}
              onMakeModelsChanged={value => handleSectionValueChanges('deviceMakeModels', value)}
              onUnknownIncludedToggled={(value: boolean) => handleUnknownIncludeChange('makeModels', value)}
              unknownIncluded={unknownIncluded.makeModels}
              unknownIncludedPermission={unknownIncludedPermission}
            />
          </SelectionableSectionWithConfirmation>
        </div>
        <div className="mobileDeviceAndConnectivity__connectionType">
          <SelectionableSectionWithConfirmation
            id={'mobileDeviceAndConnectivityConnectionTypeOptions'}
            isSectionEnabled={enabledSections.connectionTypes}
            label={formatMessage({
              defaultMessage: 'Connection type targeting options',
              id: 'targeting.mobileDeviceAndConnectivity.connectionType.label'
            })}
            modalConfirm={{
              storageKey: DO_NOT_DISTURB_TARGETING_SECTION_KEY
            }}
            onSectionEnabledChange={(enabled: boolean) => handleSectionEnabledChange('connectionTypes', enabled)}
          >
            <div
              className="mobileDeviceAndConnectivity__connectionTypeContainer"
              id={'mobileDeviceAndConnectivityConnectionTypeOptionsBox'}
            >
              <MultipleTagSelector
                borderColor="selectorLabel"
                className="mobileDeviceAndConnectiviy__connectionTypeSelector"
                defaultSelectedItems={connectionTypes}
                items={connectionTypeItems}
                mark
                onChange={(value: any) => handleSectionValueChanges('connectionTypes', value)}
                textColor="selectorLabel"
              />
            </div>
          </SelectionableSectionWithConfirmation>
        </div>
        <div className="mobileDeviceAndConnectivity__carriers">
          <SelectionableSectionWithConfirmation
            id={'mobileDeviceAndConnectivityCarriersSelectors'}
            isSectionEnabled={enabledSections.carriers}
            label={formatMessage({
              defaultMessage: 'Carrier targeting options',
              id: 'targeting.mobileDeviceAndConnectivity.carrier.label'
            })}
            modalConfirm={{
              storageKey: DO_NOT_DISTURB_TARGETING_SECTION_KEY
            }}
            onSectionEnabledChange={(enabled: boolean) => handleSectionEnabledChange('carriers', enabled)}
          >
            <div
              className="mobileDeviceAndConnectivity__carriersContainer"
              id={'mobileDeviceAndConnectivityCarriersOptionsBox'}
            >
              <Carriers
                campaignCountry={countries?.[0] ?? ''}
                carriers={carriers}
                countriesForSelector={countries}
                getDeviceCarriers={fetchDeviceCarriers}
                id={'mobileDeviceAndConnectivityCarriersCarriesSelection'}
                omitQuestion
                onCarriersChanged={value => handleSectionValueChanges('carriers', value)}
                onUnknownIncludedToggled={value => handleUnknownIncludeChange('deviceCarrier', value)}
                unknownIncluded={unknownIncluded.deviceCarrier}
                unknownIncludedPermission={unknownIncludedPermission}
              />
            </div>
          </SelectionableSectionWithConfirmation>
        </div>
      </FormExpandableSection>
    </div>
  );
}

export default MobileDeviceAndConnectivity;
